import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Home,
  Users,
  Building2,
  UserCog,
  Stethoscope,
  PlusSquare,
  FileText,
  BarChart3,
  Package,
  ClipboardList,
  Settings,
  Search,
  AlertCircle,
  ChevronDown,
  MessageCircle,
  MessageCircleWarning,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useSidebar } from "@/modules/dashboard-shell/SidebarProvider";
import { motion } from "framer-motion";
import { cn } from "@/shared/lib/cn";
import { motionTokens, springs } from "@/shared/lib/motion";
import { useNavActive } from "../useNavActive";

const iconMap = {
  home: Home,
  users: Users,
  building: Building2,
  "user-cog": UserCog,
  stethoscope: Stethoscope,
  "plus-square": PlusSquare,
  "file-text": FileText,
  "bar-chart": BarChart3,
  package: Package,
  clipboard: ClipboardList,
  settings: Settings,
  search: Search,
  "alert-circle": AlertCircle,
  "file-plus": FileText,
  "message-circle": MessageCircle,
  "message-alert": MessageCircleWarning,
} satisfies Record<IconName, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>>;

/** `pill` : le fond actif est la pastille animée (ActivePill) plutôt qu'une couleur de fond fixe. */
const navItemClass = (active: boolean, collapsed = false, pill = false) =>
  cn(
    "relative isolate flex items-center gap-3 rounded-xl text-sm font-medium transition-colors",
    // Menu replié : pastille centrée autour de l'icône.
    collapsed ? "mx-auto h-11 w-11 justify-center" : "px-4 py-3",
    active
      ? cn("font-semibold text-accent-foreground", !pill && "bg-accent shadow-sm")
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
  );

/** Pastille lime de la page active : elle glisse d'un lien à l'autre quand on navigue. */
function ActivePill() {
  return (
    <motion.span
      layoutId="sidebar-active-pill"
      className="absolute inset-0 -z-10 rounded-xl bg-accent shadow-sm"
      transition={springs.snappy}
      aria-hidden="true"
    />
  );
}

type NavLinkProps = {
  item: NavItem;
};

function NavChildLink({ child }: { child: NavChild }) {
  const { t } = useTranslation();
  const { close } = useSidebar();
  const { isEntryActive } = useNavActive();
  const isActive = isEntryActive(child.id, child.href);
  const label = t.safecheck.nav[child.id as SafecheckNavKey];

  return (
    <Link
      to={child.href}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      className={cn(
        "relative isolate flex items-center rounded-xl py-2.5 pl-11 pr-3 text-sm transition-colors",
        isActive ? "font-semibold text-accent-foreground" : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
      )}
    >
      {isActive ? <ActivePill /> : null}
      {label}
    </Link>
  );
}

export function NavLink({ item }: NavLinkProps) {
  const { t } = useTranslation();
  const { isOpen, close, open } = useSidebar();
  const { isItemActive, isParentActive } = useNavActive();
  const { id, iconName, href, children } = item;
  const Icon = iconMap[iconName];
  const label = t.safecheck.nav[id as SafecheckNavKey];
  const collapsed = !isOpen;

  const hasChildren = !!children?.length;
  const isActive = isItemActive(item);
  const parentOpen = hasChildren && isParentActive(item);
  const [expanded, setExpanded] = useState(parentOpen);

  useEffect(() => {
    if (parentOpen) setExpanded(true);
  }, [parentOpen]);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => {
            // Menu replié : on le rouvre pour afficher le sous-menu.
            if (collapsed) {
              open();
              setExpanded(true);
              return;
            }
            setExpanded(!expanded);
          }}
          title={collapsed ? label : undefined}
          className={cn(!collapsed && "w-full", navItemClass(parentOpen, collapsed))}
        >
          <Icon size={20} strokeWidth={1.75} className="shrink-0" />
          <span className={collapsed ? "sr-only" : "flex-1 text-left"}>{label}</span>
          {!collapsed && (
            <ChevronDown
              size={16}
              className={cn("transition-transform", expanded && "rotate-180")}
            />
          )}
        </button>
        {expanded && !collapsed && (
          <motion.div
            className="mt-1 space-y-0.5"
            initial={{ opacity: 0, y: -motionTokens.distance.xs }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: motionTokens.duration.fast, ease: motionTokens.easing.smooth }}
          >
            {children.map((child) => (
              <NavChildLink key={child.id} child={child} />
            ))}
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={href!}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      title={collapsed ? label : undefined}
      className={navItemClass(isActive, collapsed, true)}
    >
      {isActive ? <ActivePill /> : null}
      <Icon size={20} strokeWidth={1.75} className="shrink-0" />
      <span className={collapsed ? "sr-only" : undefined}>{label}</span>
    </Link>
  );
}
